import {FullScreenLoader, isWidgetRoute} from "utils";
import {GlobalErrorBlock} from "components/ErrorBlock/GlobalErrorBlock";
import {BootFailedModal} from "modals/BootFailedModal";
import React, {useEffect, useRef} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "stores/rootStore";
import {useAuth, type LoginCopy} from "hooks/useAuth";
import {useL10n} from "i18n/hooks";
import {LanguageBoundary} from "i18n/LanguageProvider";
import {formatCount} from "utils";
import {documentLoginCopy} from 'hooks/loginCopy';
import {FFToast} from 'ui/FFToast';
import {AlertSquareIcon} from 'icons/svg';
import {resolveText} from 'i18n/text';


type Translate = ReturnType<typeof useL10n>['t'];

// The free plan's numbers. PLANS_LIMITS is seeded from pricingDefaults.json before any
// response lands, so the fields are always there — no literal fallbacks to drift.
type FreeLimits = ReturnType<typeof useStores>['pricing']['PLANS_LIMITS']['free'];

/** The four free-account perk rows every sign-in wall lists, with `keyPerk` highlighted. */
const freePerks = (free: FreeLimits, t: Translate, keyPerk: 'words' | 'chars'): LoginCopy['perks'] => [
    {label: t('login-perk-words', {count: formatCount(free.granted_words_simple_scan)}), isKey: keyPerk === 'words'},
    {label: t('login-perk-deep-scans', {count: free.deep_scans})},
    {label: t('login-perk-chars', {chars: formatCount(free.words_per_generation_limit)}), isKey: keyPerk === 'chars'},
    {label: t('login-perk-files', {mb: free.document_upload_limit_mb})},
];

/**
 * The login view's lines for the character-cap wall (Figma `Log in / Sign up`): the
 * cap an account gets, against the one an anon has, with the numbers read off the
 * plans rather than baked into the package's copy. Built on the prompt, not per render.
 */
const charsWallCopy = (root: ReturnType<typeof useStores>, t: Translate): LoginCopy => {
    const free = root.pricing.PLANS_LIMITS.free;
    return {
        badge: null,
        title: t('login-chars-title', {chars: formatCount(free.words_per_generation_limit)}),
        subtitle: t('login-chars-subtitle', {chars: formatCount(root.user.user?.words_per_generation_limit ?? free.words_per_generation_limit)}),
        perks: freePerks(free, t, 'chars'),
        footer: t('login-footer-free'),
    };
};

/**
 * The login view's lines for the Humanizer's sign-in wall (Figma `0.1.2`): the
 * free Humanizer words on offer up top (the visitor's A/B arm while that test enrols),
 * the standard free-account perks below.
 */
const humanizerWallCopy = (root: ReturnType<typeof useStores>, t: Translate): LoginCopy => {
    const free = root.pricing.PLANS_LIMITS.free;
    return {
        badge: null,
        title: t('login-humanizer-title', {count: formatCount(root.pricing.freeHumanizerWords)}),
        subtitle: t('login-humanizer-subtitle'),
        perks: freePerks(free, t, 'words'),
        footer: t('login-footer-free'),
    };
};

const LOGIN_COPY_BY_REASON: Record<string, (root: ReturnType<typeof useStores>, t: Translate) => LoginCopy> = {
    chars: charsWallCopy,
    humanizer: humanizerWallCopy,
    document: root => documentLoginCopy(root.pricing.PLANS_LIMITS.free.document_upload_limit_mb),
};

/** The toast a store raised (`root.toast`), over every page. */
const AppToast = observer(() => {
    const {toast} = useStores();
    const {t} = useL10n();
    const current = toast.toast;
    if (!current) return null;
    return (
        <FFToast className="app-toast" data-testid="app-toast" tone="error" icon={<AlertSquareIcon/>}
                 title={resolveText(current.title)} onClose={() => toast.dismiss()} closeLabel={t('common-close')}>
            {resolveText(current.body)}
        </FFToast>
    );
});

const LoginPromptWatcher = observer(() => {
    const root = useStores();
    const {input} = root;
    const {login} = useAuth();
    const {t} = useL10n();
    const loginPromptCounter = input.loginPromptCounter;
    useEffect(() => {
        if (loginPromptCounter > 0) {
            login({
                reason: input.loginPromptReason,
                copy: LOGIN_COPY_BY_REASON[input.loginPromptReason]?.(root, t),
                continueScan: input.loginContinueScan,
                action: input.loginAction,
                resume: input.loginResume,
                analyticsContext: input.loginAnalyticsContext,
            });
            input.loginPromptCounter = 0;
            input.loginContinueScan = false;
            input.loginAction = null;
            input.loginResume = null;
            input.loginAnalyticsContext = undefined;
        }
    }, [loginPromptCounter]);
    return null;
})

// Landing's exit-intent popup redirects here with `?open_signup=1` to carry the
// sign-up intent through. Capture that flag once (stripping it so a refresh or
// shared link can't re-pop the modal), then — once anon auth has settled — fire
// the standard login prompt. promptLogin is anon-gated, so an already-signed-in
// visitor is never nagged.
const ExitIntentLoginWatcher = observer(() => {
    const {input, user} = useStores();
    const wantsSignupRef = useRef(false);
    const firedRef = useRef(false);

    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            if (!params.get('open_signup')) return;
            wantsSignupRef.current = true;
            params.delete('open_signup');
            const qs = params.toString();
            const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash;
            window.history.replaceState({}, '', newUrl);
        } catch (_) { /* URL/history unavailable — skip, no prompt */ }
    }, []);

    // Gate on isReady() (token + user loaded + initialized), NOT just
    // `initialized`: auth flips `initialized` synchronously via setToken before
    // refresh() populates `user`, and promptLogin no-ops while `user` is null.
    const userReady = user.isReady();
    useEffect(() => {
        if (firedRef.current || !wantsSignupRef.current) return;
        if (!userReady) return;
        firedRef.current = true;
        input.promptLogin('general');
    }, [userReady, input]);

    return null;
})

/**
 * No guest session could be created — most often the IP is out of anonymous users. Signing in
 * is the one route that still works (it brings a session of its own), so the app offers it
 * rather than waiting on a session that will never arrive. Persistent: behind the modal there
 * is nothing to go back to.
 */
const AnonAuthBlockedWatcher = observer(() => {
    const {user} = useStores();
    const {login} = useAuth();
    const firedRef = useRef(false);

    useEffect(() => {
        if (!user.anonAuthBlocked || firedRef.current) return;
        firedRef.current = true;
        login({persistent: true});
    }, [user.anonAuthBlocked, login]);

    return null;
})

export const GlobalMechanics = observer(() => {
    const root = useStores();
    // When we leave for Stripe (e.g. guest checkout) we keep the full-screen
    // loader on to cover the redirect. If the user hits the browser Back button
    // the page is restored from the bfcache as a frozen snapshot — React never
    // remounts, so isLoading stays true and the loader hangs forever. The only
    // signal on a bfcache restore is pageshow with persisted=true: clear the
    // loader there so coming back never strands the user on a spinner.
    useEffect(() => {
        const onPageShow = (e: PageTransitionEvent) => {
            if (e.persisted) root.setLoading(false);
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, [root]);
    return <>
        <FullScreenLoader loading={root.isLoading}/>
        <GlobalErrorBlock/>
        <AppToast/>
        {!isWidgetRoute() && <LoginPromptWatcher/>}
        {!isWidgetRoute() && <ExitIntentLoginWatcher/>}
        {!isWidgetRoute() && <AnonAuthBlockedWatcher/>}
        {!isWidgetRoute() && <BootFailedModal/>}
        <LanguageBoundary>{root.modal.modalComponent}</LanguageBoundary>
    </>
})
